import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { errorLogger } from "@/Common/helpers/logger";
import { apiInstance, gql } from "@/services/httpAdapter";
import { ISalesWashout } from "@/Washouts/types/Api/Sales";

// Common fragments
import {
	AddedCompensationFragment,
	CallActivityPerformanceFragment,
	ChargebacksFragment,
	CompensationFragment,
	EmployeeFragment,
	LongevityFragment,
	ReviewFragment,
	SalesPerformanceFragment,
} from "@/Washouts/api/common/fragments";

type Params = string[];

export async function getSalesWashoutReview(...params: Params) {
	const [locationId, userId, commissionCycleId, payPeriodId, position] = params;
	const query = gql`
		query getSalesWashout(
			$locationId: String!
			$userId: String!
			$commissionCycleId: String!
			$payPeriodId: String!
			$position: String!
		) {
			getSalesWashout(
				locationId: $locationId
				userId: $userId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				position: $position
			) {
				_id
				review {
					status
					statusSetBy {
						fullName
					}
				}
			}
		}
	`;

	const variables = {
		locationId,
		userId,
		commissionCycleId,
		payPeriodId,
		position,
	};

	try {
		const response: GraphqlQueryResponse<"getSalesWashout", Pick<ISalesWashout, "_id" | "review">[]> = await apiInstance.post(
			"/graphql",
			{
				query,
				variables,
			}
		);
		return response.data.data?.getSalesWashout;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}

export async function getSalesAssociateWashout(...params: Params) {
	const [locationId, userId, commissionCycleId, payPeriodId, position] = params;
	const query = gql`
		query getSalesWashout(
			$locationId: String!
			$userId: String!
			$commissionCycleId: String!
			$payPeriodId: String!
			$position: String!
		) {
			getSalesWashout(
				locationId: $locationId
				userId: $userId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				position: $position
			) {
				_id
				commissionCycleId
				payPeriodId
				created
				overdraw
				review {
					...Review
				}
				employee {
					...Employee
				}
				salesPerformance {
					...SalesPerformance
				}
				carryOver {
					sales {
						_id
						saleDate
						dealNumber
						customerName
						customerNumber
						salesManagerId
						salesManagerName
						stockNumber
						description
						saleType
						dealType
						adjustable
						frontGrossProfit
						backGrossProfit
						units
						saleAmount
						vehicleInformation {
							make
							model
							modelYear
							trim
						}
						frontProducts {
							_id
							product
							value
						}
						backProducts {
							_id
							product
							value
						}
						frontCommission
						backCommission
						grossCommission
						funded
						external
						origin {
							icon
							tooltip
						}
					}
					totals {
						totalsTypeTitle
						frontGrossProfit
						backGrossProfit
						frontProducts {
							_id
							product
							total
						}
						backProducts {
							_id
							product
							total
						}
						frontCommission
						backCommission
						grossCommission
						units
					}
				}
				salesLog {
					sales {
						_id
						saleDate
						dealStatus
						financeManagerName
						dealNumber
						dealManagerName
						stockNumber
						saleType
						frontGrossProfit
						backGrossProfit
						units
						frontProducts {
							product
							value
						}
						backProducts {
							product
							value
						}
					}
					totals {
						frontGrossProfit
						backGrossProfit
						frontProducts {
							product
							total
						}
						backProducts {
							product
							total
						}
						units
					}
				}
				callActivityPerformance {
					...WashoutCallActivityPerformance
				}
				newChargebacks {
					...Chargebacks
				}
				preOwnedChargebacks {
					...Chargebacks
				}
				deductions {
					deductions {
						_id
						title
						value
					}
					total
				}
				birdDogs {
					birdDogs {
						_id
						date
						customer
						amount
						stockNumber
					}
					total
				}
				spiffs {
					spiffs {
						_id
						date
						spiffName
						spiffType
						amount
						stocksNumbers
					}
					total
				}
				autoAlerts {
					autoAlerts {
						_id
						date
						customer
						amount
						stockNumber
						saleType
						lastActivityDate
						actionType
						initiatedBy
						associationWindow
					}
					total
				}
				customerSatisfactionIndex {
					totalReviews
					isPresent
					makes {
						_id
						make
						requiredReviews
						penetration
					}
				}
				longevity {
					...Longevity
				}
				addedCompensations {
					...AddedCompensation
				}
				summary {
					...Compensation
				}
			}
		}
		${AddedCompensationFragment}
		${CallActivityPerformanceFragment}
		${ChargebacksFragment}
		${CompensationFragment}
		${EmployeeFragment}
		${LongevityFragment}
		${ReviewFragment}
		${SalesPerformanceFragment}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		userId,
		payPeriodId,
		position,
	};

	try {
		const response: GraphqlQueryResponse<"getSalesWashout", ISalesWashout> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getSalesWashout;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}
